import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  InputProps,
} from '@chakra-ui/react'
import React from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'

interface FormCurrencyInputProps<T extends FieldValues>
  extends Omit<InputProps, 'name' | 'value' | 'onChange'> {
  name: Path<T>
  label: string
  control: Control<T, any, any>
  isRequired?: boolean
}

export function FormCurrencyInput<T extends FieldValues>({
  name,
  label,
  control,
  isRequired = false,
  ...inputProps
}: FormCurrencyInputProps<T>) {
  const {
    field: { value, onChange, ...field },
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value.replace(/\D/g, '') // Remove non-numeric characters
    // Always pad with zeros to ensure at least 3 digits
    const padded = inputValue.padStart(3, '0')
    const integerPart = padded.slice(0, -2)
    const decimalPart = padded.slice(-2)
    onChange(`${integerPart}.${decimalPart}`)
  }

  const displayValue = (value: string | number) => {
    const castedValue = typeof value === 'number' ? `${value}` : value
    // Remove leading zeros from integer part
    const [integer = '0', decimal = '00'] = castedValue.split('.')
    const cleanInteger = integer.replace(/^0+/, '') || '0'
    const cleanDecimal = (decimal + '00').slice(0, 2)
    return `${cleanInteger},${cleanDecimal}`
  }

  return (
    <FormControl isInvalid={!!error} isRequired={isRequired}>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <InputLeftElement pointerEvents="none" color="gray.500" fontSize="sm">
          R$
        </InputLeftElement>
        <Input
          onFocus={(e) => e.target.select()} // Select input on focus
          {...field}
          {...inputProps}
          value={displayValue(value)}
          onChange={(event) => handleChange(event)}
          placeholder="0,00"
        />
      </InputGroup>
      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  )
}
